import React, { useState, useRef, useEffect } from 'react';
import { styled } from '../../../styles/stitches.config'; // Adjust path if needed
import { useUser } from '../../../contextManager/user.context';
import useAuth from '../../../hooks/auth.service';
import { useNavigate } from 'react-router-dom';
import { PersonFill, DoorClosedFill, GearFill } from 'react-bootstrap-icons';

// 1. Import the NavItem style from your existing navigation styles
import { IconTouchTargetExpander, ExpandedNavItem } from '../../../styles/navigation.style';
import { GenericNavigationBar, NavigationBarProps } from '../types';
import { getInitials } from '../../../utils/name';


// --- STYLED COMPONENTS ---
const AvatarCircle = styled('button', {
  // Reset button styles to make it look like a div
  all: 'unset', 
  boxSizing: 'border-box',
  
  // Avatar styles from before
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '42px',
  height: '42px',
  borderRadius: '100%',
  cursor: 'pointer',
  backgroundColor: '#d1fae5',
//   backgroundColor: '#a7f3d0',
//   backgroundColor: '#6ee7b7',
  border: '2px solid #34d399',
  color: '#065f46',
  fontSize: '1rem',
  fontWeight: 'bold',
  
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
      boxShadow: '0 0 0 3px rgba(52, 211, 153, 0.4)',
  },
  '&:focus-visible': { // For accessibility
      boxShadow: '0 0 0 3px rgba(52, 211, 153, 0.4)',
  }
});

const DropdownPanel = styled('div', {
    position: 'absolute',
    top: 'calc(100% + 5px)', // Position below the avatar with a small gap
    right: 0,
    width: '200px',
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
    zIndex: 100,
    overflow: 'hidden', // Ensures children conform to border radius
    border: '1px solid $gray4',
    fontSize: '$sm',


});


interface InitialsDropdownProps {
  isMobile: boolean;
  reactLinks: NavigationBarProps['reactRouterLinks'];
}
// --- MAIN COMPONENT ---
export const InitialsAvatarDropdown: React.FC<InitialsDropdownProps> = ({isMobile, reactLinks})=> {
  // 2. This logic is taken directly from your HamburgerSettingsDropdown
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { userDetails } = useUser();
  const { logoutUser } = useAuth();
  const navigate = useNavigate();
  
  const initials = getInitials(userDetails?.name);

  // Effect to close the menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);


  // Helper to handle navigation and closing the menu
  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  }

  return (
    // Use a div as the relative container for the absolute-positioned panel
    <div style={{ position: 'relative' }} ref={menuRef}>

      {/* 3. The trigger is now our styled AvatarCircle */}
      <AvatarCircle onClick={() => setIsOpen(prev => !prev)} aria-label="Open user menu">
        {initials}
      </AvatarCircle>

      {/* 4. The dropdown panel uses the same conditional rendering */}
      {isOpen && !isMobile && (
        <>
        <DropdownPanel>
          <ExpandedNavItem onClick={() => handleNavigation('/profile')}>
            <IconTouchTargetExpander><PersonFill size={15} /></IconTouchTargetExpander>
            <span>Profile</span>
          </ExpandedNavItem>
          <ExpandedNavItem onClick={() => handleNavigation('/profile?tab=Settings')}>
            <IconTouchTargetExpander><GearFill size={15} /></IconTouchTargetExpander>
            <span>Settings</span>
          </ExpandedNavItem>
          <ExpandedNavItem onClick={() => {
              logoutUser();
              setIsOpen(false);
          }}>
            <IconTouchTargetExpander><DoorClosedFill size={15} /></IconTouchTargetExpander>
            <span>Logout</span>
          </ExpandedNavItem>


        </DropdownPanel>
        </>
      )}

      {isOpen && isMobile && (
        <>
        <DropdownPanel>

        {reactLinks.map(({ to, title, icon: Icon }, idx) => (
          <ExpandedNavItem onClick={() => handleNavigation(to as string)}>
          <IconTouchTargetExpander> <Icon size={20}/> </IconTouchTargetExpander>
          <span>{title}</span>
          </ExpandedNavItem>
          ))}

          <ExpandedNavItem onClick={() => handleNavigation('/profile?tab=Settings')}>
            <IconTouchTargetExpander><GearFill size={20} /></IconTouchTargetExpander>
            <span>Settings</span>
          </ExpandedNavItem>
          <ExpandedNavItem onClick={() => {
              logoutUser();
              setIsOpen(false);
          }}>
            <IconTouchTargetExpander><DoorClosedFill size={20} /></IconTouchTargetExpander>
            <span>Logout</span>
          </ExpandedNavItem>

        </DropdownPanel>
        </>
      )}

      
    </div>
  );
};